// 「直感に従う」の振り返り: 日付順の一覧と簡単な集計（有料・ログイン中のみ）
import React, { useEffect, useMemo, useState } from 'react';
import { ENTRIES, BODY_OPTIONS, getBodyGap, BodyReaction } from './suggestions';
import { listChoices, DailyChoiceRow } from './dailyChoices';
import { PlaceRecord } from './NearbyCard';

type Props = {
  isPremium: boolean;
  authUser: any;
  onUnlock: () => void;
  onLogin: () => void;
};

const card: React.CSSProperties = { background: 'white', borderRadius: '16px', border: '1px solid #fde68a', padding: '16px', marginBottom: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const h2: React.CSSProperties = { fontSize: '16px', fontWeight: 'bold', fontFamily: 'serif', color: '#78350f', marginBottom: '10px' };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: '#d97706', fontSize: '14px', cursor: 'pointer', padding: '8px', textDecoration: 'underline' };
const bigBtn: React.CSSProperties = { width: '100%', minHeight: '52px', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: 'white', fontSize: '17px', fontWeight: 'bold', fontFamily: 'serif', cursor: 'pointer' };

const ENTRY_LABEL: Record<string, string> = Object.fromEntries([...ENTRIES.map(e => [e.id, `${e.icon} ${e.label}`]), ['coin', '🪙 コイントス']]);
const BODY_LABEL: Record<string, string> = Object.fromEntries(BODY_OPTIONS.map(o => [o.id, o.label]));
const COIN_FEELING: Record<string, string> = { relieved: 'ほっとした', disappointed: 'がっかり' };

const dayKey = (iso: string) => { const d = new Date(iso); return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${'日月火水木金土'[d.getDay()]}）`; };
const timeOf = (iso: string) => { const d = new Date(iso); return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`; };

function Bar({ label, n, max }: { label: string; n: number; max: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
      <div style={{ width: '88px', fontSize: '14px', color: '#374151', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: '14px', background: '#f3f4f6', borderRadius: '7px', overflow: 'hidden' }}>
        <div style={{ width: max ? `${(n / max) * 100}%` : 0, height: '100%', background: '#f59e0b', borderRadius: '7px' }} />
      </div>
      <div style={{ width: '28px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>{n}</div>
    </div>
  );
}

export default function ReflectTab({ isPremium, authUser, onUnlock, onLogin }: Props) {
  const [rows, setRows] = useState<DailyChoiceRow[] | null>(null);
  const [openRecord, setOpenRecord] = useState<string | null>(null);
  const canView = !!(isPremium && authUser?.id);

  useEffect(() => {
    if (!canView) return;
    listChoices().then(setRows);
  }, [canView, authUser?.id]);

  const stats = useMemo(() => {
    const judged = (rows ?? []).filter(r => r.entry_type !== 'coin' && r.excited != null);
    const body: Record<string, number> = { light: 0, heavy: 0, forward: 0, restless: 0 };
    judged.forEach(r => { if (r.body_reaction) body[r.body_reaction]++; });
    const byEntry = ENTRIES.map(e => {
      const rs = judged.filter(r => r.entry_type === e.id);
      return { ...e, excited: rs.filter(r => r.excited).length, total: rs.length };
    }).filter(e => e.total > 0).sort((a, b) => b.excited - a.excited);
    const gaps = judged.filter(r => r.body_reaction && getBodyGap(!!r.excited, r.body_reaction as BodyReaction)).length;
    const coins = (rows ?? []).filter(r => r.entry_type === 'coin' && r.coin_feeling);
    return {
      judged: judged.length, body, byEntry, gaps,
      relieved: coins.filter(r => r.coin_feeling === 'relieved').length,
      disappointed: coins.filter(r => r.coin_feeling === 'disappointed').length,
    };
  }, [rows]);

  const grouped = useMemo(() => {
    const g: { day: string; items: DailyChoiceRow[] }[] = [];
    (rows ?? []).forEach(r => {
      const d = dayKey(r.created_at);
      if (g.length && g[g.length - 1].day === d) g[g.length - 1].items.push(r);
      else g.push({ day: d, items: [r] });
    });
    return g;
  }, [rows]);

  const header = (
    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
      <div style={{ fontSize: '22px', fontWeight: 900, fontFamily: 'serif', color: '#78350f' }}>直感の振り返り</div>
      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>この記録は、あなただけが見られます</div>
    </div>
  );
  const goIntuition = () => window.dispatchEvent(new CustomEvent('navigate-tab', { detail: 'intuition' }));

  if (!isPremium) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '6px 2px 24px' }}>
        {header}
        <div style={{ ...card, textAlign: 'center', fontSize: '15px', color: '#6b7280', lineHeight: 1.8 }}>
          記録を残して振り返るのは、プレミアムで使えます。<br />どんなときにワクワクして、身体がどう感じていたか、あとから見返せます。
          <div><button onClick={onUnlock} style={linkBtn}>プレミアムについて見る</button></div>
        </div>
      </div>
    );
  }
  if (!authUser?.id) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '6px 2px 24px' }}>
        {header}
        <div style={{ ...card, textAlign: 'center', fontSize: '15px', color: '#6b7280', lineHeight: 1.8 }}>
          記録はログインしているときに残ります。
          <div style={{ marginTop: '12px' }}><button onClick={onLogin} style={bigBtn}>ログインする</button></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '6px 2px 24px', animation: 'fadeIn 0.3s' }}>
      {header}
      {rows === null && <div style={{ textAlign: 'center', color: '#6b7280', padding: '30px' }}>読み込み中...</div>}

      {rows && rows.length === 0 && (
        <div style={{ ...card, textAlign: 'center', fontSize: '15px', color: '#6b7280', lineHeight: 1.8 }}>
          まだ記録がありません。<br />「直感に従う」を使うと、ここに残っていきます。
          <div style={{ marginTop: '12px' }}><button onClick={goIntuition} style={bigBtn}>直感に従う</button></div>
        </div>
      )}

      {rows && rows.length > 0 && (
        <>
          {stats.judged > 0 && (
            <div style={card}>
              <div style={h2}>ワクワクが多かった入口</div>
              {stats.byEntry.slice(0, 3).map(e => (
                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', padding: '6px 0', borderBottom: '1px solid #f9fafb' }}>
                  <span>{e.icon} {e.label}</span>
                  <span style={{ color: '#d97706', fontWeight: 'bold' }}>{e.excited}<span style={{ color: '#9ca3af', fontWeight: 'normal' }}> / {e.total}回</span></span>
                </div>
              ))}
            </div>
          )}

          {stats.judged > 0 && (
            <div style={card}>
              <div style={h2}>身体の反応</div>
              {BODY_OPTIONS.map(o => <Bar key={o.id} label={`${o.icon} ${o.label}`} n={stats.body[o.id]} max={Math.max(...Object.values(stats.body))} />)}
              {stats.gaps > 0 && (
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', lineHeight: 1.7 }}>
                  頭と身体の答えが食い違ったのは {stats.gaps} 回。そのときの記録を見返すと、何か見えてくるかもしれません。
                </div>
              )}
            </div>
          )}

          {stats.relieved + stats.disappointed > 0 && (
            <div style={card}>
              <div style={h2}>コイントスのあとの気持ち</div>
              <Bar label="ほっとした" n={stats.relieved} max={Math.max(stats.relieved, stats.disappointed)} />
              <Bar label="がっかり" n={stats.disappointed} max={Math.max(stats.relieved, stats.disappointed)} />
            </div>
          )}

          {grouped.map(g => (
            <div key={g.day} style={card}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#78350f', marginBottom: '8px' }}>{g.day}</div>
              {g.items.map(r => (
                <div key={r.id} style={{ padding: '10px 0', borderTop: '1px solid #f3f4f6', fontSize: '14px', color: '#374151', lineHeight: 1.7 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280', fontSize: '13px' }}>
                    <span>{ENTRY_LABEL[r.entry_type] ?? r.entry_type}</span><span>{timeOf(r.created_at)}</span>
                  </div>
                  {r.entry_type === 'coin' ? (
                    <div>
                      {r.coin_options?.join(' か ')} → <b>{r.coin_result}</b>
                      {r.coin_feeling && <span style={{ color: '#d97706' }}>（{COIN_FEELING[r.coin_feeling]}）</span>}
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: '15px', color: '#1f2937' }}>{r.suggestion}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                        {r.excited != null && (
                          <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '12px', background: r.excited ? '#fef3c7' : '#f3f4f6', color: r.excited ? '#92400e' : '#6b7280' }}>
                            {r.excited ? 'ワクワク' : 'ちょっと違う'}
                          </span>
                        )}
                        {r.body_reaction && <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '12px', background: '#f0f9ff', color: '#0369a1' }}>身体：{BODY_LABEL[r.body_reaction]}</span>}
                        {r.direction && <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '12px', background: '#f0fdf4', color: '#15803d' }}>🧭 {r.direction}</span>}
                      </div>
                      {r.free_text && <div style={{ marginTop: '6px', padding: '8px 10px', background: '#f9fafb', borderRadius: '8px', color: '#4b5563' }}>「{r.free_text}」</div>}
                      {r.place_type && <div style={{ marginTop: '4px', color: '#4b5563' }}>行った場所：{r.place_type}{r.mood_after && ` ／ 気分：${r.mood_after}`}</div>}
                      {r.entry_type === 'travel' && r.excited && !r.place_type && (
                        openRecord === r.id
                          ? <PlaceRecord rowId={r.id} onSaved={(place, mood) => {
                              setRows(rs => (rs ?? []).map(x => x.id === r.id ? { ...x, place_type: place, mood_after: mood } : x));
                              setOpenRecord(null);
                            }} />
                          : <button onClick={() => setOpenRecord(r.id)} style={{ ...linkBtn, padding: '4px 0' }}>行ってきたら記録する</button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </>
      )}
      <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', lineHeight: 1.7, marginTop: '18px' }}>
        気の話は参考であり、医療・介護・法律の判断の代わりにはしません。
      </div>
    </div>
  );
}
